import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
    faAddressBook,
    faBook,
    faBorderAll,
    faBoxesStacked,
    faCalendarPlus,
    faChalkboardTeacher,
    faChartPie,
    faCircleQuestion,
    faComments,
    faCompress,
    faExpand,
    faExternalLinkAlt,
    faEye,
    faEyeSlash,
    faFileArchive,
    faFileCode,
    faFileImport,
    faFileInvoiceDollar,
    faFileSignature,
    faGavel,
    faKey,
    faKeyboard,
    faList,
    faNewspaper,
    faPaintBrush,
    faPiggyBank,
    faPlus,
    faSearch,
    faStoreAlt,
    faTable,
    faToolbox,
    faUserPlus,
    faUsersCog,
    faWindowMaximize
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InfoTooltip, ScrollArea, SideBarAccordion, Spinner } from '@ui';
import Tooltip from '@ui/Tooltip';
import { bankingKeys } from 'components/Banking/shared/api';
import { CheckAccess } from 'components/Clubs/ClubPermissionManagement/CheckAccess';
import { usePlanScopeStore } from 'components/TrainingPlanner/usePlanScope';
import { LanguageDropdown } from 'components/Language';
import { useIssuers, useMandanten } from 'components/UserInvoice/data';
import { AnimatePresence, motion } from 'framer-motion';
import { hasHiddenModules, isModuleVisible } from 'helpers/clubModules';
import useModalManager from 'hooks/useModalManager';
import useShowAllModules from 'hooks/useShowAllModules';
import { useVisibleModules } from 'hooks/useVisibleModules';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSettingsSectionNavItems } from '../../CommandPalette/settingsSectionsNavItems';
import useCommandPaletteStore from '../../CommandPalette/useCommandPaletteStore';
import GlobalOverlay from '../../GlobalOverlay';
import type { Page } from './clubPages';
import { buildClubPages } from './clubPages';
import { GlobalSearch } from './GlobalSearch';
import HiddenModuleBanner from './HiddenModuleBanner';
import useFavorites from './useFavorites';

const InvoiceForm = dynamic(() => import('components/UserInvoice/InvoiceForm/InvoiceForm'));
const ClubMemberForm = dynamic(() =>
    import('components/ClubMemberManagement/ClubMember/ClubMemberForm/ClubMemberForm').then((m) => m.ClubMemberForm)
);
const EntryForm = dynamic(() => import('components/Trainingsjournal/EntryForm/EntryForm'));
const TrainingForm = dynamic(() => import('components/Trainingsjournal/Training/TrainingForm'));
const EventForm = dynamic(() => import('components/EventCalendar/Event/EventForm/EventForm'));
const AllocationForm = dynamic(() => import('components/SpaceManager/AllocationForm/AllocationForm'));
const VoucherForm = dynamic(() => import('components/Vouchers/VoucherForm/VoucherForm'));
const VirtualKeyForm = dynamic(() => import('components/AccessControl/VirtualKey/VirtualKeyForm'));
const PostForm = dynamic(() => import('components/Feed/PostForm'));
const ManualTrainerBookingForm = dynamic(() =>
    import('components/TrainerBooking/TrainerBooking/ManualTrainerBookingForm').then((m) => m.ManualTrainerBookingForm)
);
const MessageComponent = dynamic(() =>
    import('components/MessageManagement/MessageComponent/MessageComponent').then((m) => m.MessageComponent)
);
const ShortcutsPanel = dynamic(() => import('~/components/ShortcutsPanel/ShortcutsPanel'));

import useAuth from '@hooks/useAuth';
import { useExperimental } from '@hooks/useExperimental';
import { useEventPermissions } from 'components/EventCalendar/Event/hooks/useEventPermissions';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import Dropdown from '@ui/Dropdown';
import DropdownItem from '@ui/Dropdown/DropdownItem';
import { useClubSlug } from 'components/Clubs/hooks';
import { FormularMigrationModal } from 'components/Formular/FormularMigrationModal';
import { imageSource } from 'helpers/ImageHelper';
import Image from 'next/image';
import Link from 'next/link';
import useSWR from 'swr';
import AnnouncementModal from '~/components/AnnouncementModal/AnnouncementModal';
import ChangelogArchive from '~/components/Changelog/ChangelogArchive';
import ChangelogPopup from '~/components/Changelog/ChangelogPopup';
import { CheckSubscription } from '~/components/Clubs/ClubPermissionManagement/CheckSubscription/CheckSubscription';
import { AppSupportChatSplitLayout } from '~/components/ui/Chatbot/app-support-chat/AppSupportChatSplitLayout';
import type { SubscriptionStatusData } from '~/hooks/useSubscriptionStatus';

export type Tab = {
    title: string;
    key: string;
    id?: string;
    tags?: string[];
    pageLink?: string;
    pageTitle?: string;
    icon?: IconDefinition;
    content?: React.ReactNode;
    badge?: number;
    beta?: boolean;
    experimental?: string;
    permissions?: string[] | string;
    /** Explicit destination. When set, the tab links here instead of `${page.link}/${key}`.
     *  Used by the Einstellungen hub to point at each module's existing settings route. */
    link?: string;
    /** Renders the tab as an external link (opens in a new tab, shows an external-link icon,
     *  never marked active). Used for links that leave the module, e.g. Developer Docs. */
    external?: boolean;
    /** Tabs marked 'settings' are grouped behind the gear icon in the accordion header,
     *  separating rarely-used configuration from day-to-day actions. */
    group?: 'settings';
    /** Extra first-path-segments below the module that should still mark this tab active.
     *  For modules with routes nested deeper than `${page.link}/${key}` — e.g. the training
     *  planner's `/training-planner/plan/{id}/…`, which belongs to its plans tab. */
    activeKeys?: string[];
    /** Short explanation shown as ⓘ icon next to the tab title — use for Fachbegriffe. */
    tooltip?: string;
};

interface Props {
    pages?: Page[];
    children?: React.ReactNode;
    permissions?: string[] | string;
    pageReturnLink?: string;
}

// Module scope on purpose, not a ref: every club page renders its own ModulePageLayout, so a
// page change remounts this component. A ref would forget that the migration gate is already
// standing and re-create it on every navigation, throwing away the assignments made so far.
let migrationModalOpened = false;

export const ModulePageLayout: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    pages: _pages,
    permissions,
    children,
    pageReturnLink
}) => {
    const clubSlug = useClubSlug();
    const { t } = useTranslation('common');
    const { user, isAuthenticated } = useAuth();
    const experimentalFeatures = useExperimental();
    const { addModal, setModal, removeModal } = useModalManager();
    const { pathname, asPath, events, query, push } = useRouter();
    const currentPath = asPath.split('?')[0];

    /**
     * The week grid of the plan this club last worked in.
     *
     * The planner remembers that per club in its own store, which is also what
     * the module entry falls back to — so this lands where re-entering the
     * module would land, one step further in. Without a remembered plan it
     * opens the chooser, which is the only honest answer: there is no week
     * without a plan.
     */
    const openPlannerWeek = useCallback(() => {
        const planId = usePlanScopeStore.getState().selectedByClub[clubSlug ?? ''];

        return push(
            planId ? `/club/${clubSlug}/training-planner/plan/${planId}` : `/club/${clubSlug}/training-planner`
        );
    }, [clubSlug, push]);
    const accordionRef = useRef<any | null>(null);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [pageLogoError, setPageLogoError] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const { favorites, addFavorite, removeFavorite, resetFavorites } = useFavorites('favorites-storage');
    const experimentalFeatureKey = experimentalFeatures.join('\0');
    const experimentalFeatureSet = useMemo(() => new Set(experimentalFeatures), [experimentalFeatureKey]);

    const pathSegments = useMemo(() => pathname.split('/'), [pathname]);
    const currentPageLink = pathSegments[3] ?? '';
    const currentTabKey = pathSegments[4] ?? '';

    const pageIsActive = useCallback((link = '') => currentPageLink === link, [currentPageLink]);
    const isActive = useCallback(
        (pageLink = '', key = '', activeKeys?: string[]) =>
            currentPageLink === pageLink &&
            ((key || '') === currentTabKey || true === activeKeys?.includes(currentTabKey)),
        [currentPageLink, currentTabKey]
    );

    const { data: club, isLoading: isLoadingClub } = useSWR(`clubs/${clubSlug}`);
    const { data: updateCount } = useSWR<{ data: number }>(
        isAuthenticated && clubSlug ? `club/${clubSlug}/journal/update/count` : null
    );
    // Open space manager booking requests — drives the badge on the "Buchungsanfragen" tab.
    // Without the approve permission this 403s as PermissionDenied, which the global fetcher
    // swallows silently, so the count stays undefined and no badge renders.
    const { data: allocationRequestCount } = useSWR<{ count: number }>(
        isAuthenticated && clubSlug ? `club/${clubSlug}/space-manager/allocation/requests/count` : null
    );
    // Unread event bookings — drives the badge on the "Buchungen" tab. 403s without the view
    // permission, which the global fetcher swallows, so the badge simply stays hidden.
    const { data: unreadBookingCount } = useSWR<{ count: number }>(
        isAuthenticated && clubSlug ? `booking/${clubSlug}/unread/count` : null
    );
    // Unread mails in the club inbox, 403s without message.inbox.view like the counts above.
    const { data: unreadInboxCount } = useSWR<{ count: number }>(
        isAuthenticated && clubSlug ? `club/${clubSlug}/inbox/unread/count` : null
    );
    // the module is visible with inbox rights alone, composing needs message.view
    const { hasAccess: canComposeMessage } = useCheckPermissions({ clubSlug, permission: 'message.view' });
    const { canCreate: canCreateEvent } = useEventPermissions(clubSlug, isAuthenticated);
    const { data: subscriptionStatus } = useSWR<SubscriptionStatusData>(
        isAuthenticated && clubSlug ? `subscriptions/status/${clubSlug}` : null
    );
    // Default to [] while loading to avoid flash; CheckSubscription shares this SWR key (cache hit)
    const subscribedPlans = subscriptionStatus?.subscribed_plans ?? [];
    // Fourth navigation axis — which modules the club chose to see. `undefined` while
    // loading and `null` when never configured both mean "show everything".
    const { visible: visibleModules, mutate: mutateVisibleModules } = useVisibleModules(
        clubSlug,
        Boolean(isAuthenticated)
    );
    // Per-user, per-session escape hatch. Never changes the club setting.
    const showAllModules = useShowAllModules((state) => state.showAll);
    const setShowAllModules = useShowAllModules((state) => state.setShowAll);
    const { data: dataIssuers } = useIssuers(clubSlug);
    const hasSetting = dataIssuers?.data?.length > 0;
    // Only clubs that already write invoices can keep books, so the others never send this request.
    const { mandanten } = useMandanten(isAuthenticated && hasSetting ? clubSlug : null);
    const accountingLive = mandanten.some((mandant) => mandant.accounting_enabled);
    // Books run along in trial mode for everyone, so the tabs stay in the main nav; only binding start drops the beta mark.
    const accountingTabProps = useMemo(() => ({ beta: !accountingLive }), [accountingLive]);

    const { data: migrationStatus } = useSWR<{ needs_migration: boolean }>(
        isAuthenticated ? 'user/formular/migration-status' : null
    );

    useEffect(() => {
        // Nothing left to migrate — arm the gate again for whoever logs in next in this tab.
        if (!migrationStatus?.needs_migration) {
            migrationModalOpened = false;
            return;
        }

        if (migrationModalOpened) return;

        migrationModalOpened = true;
        setModal({
            title: 'Formulare den Clubs zuweisen',
            preventClose: true,
            size: 60,
            content: (id) => <FormularMigrationModal onDone={() => removeModal(id)} />
        });
    }, [migrationStatus]);

    const issuerTab: Tab = useMemo(
        () => ({
            key: 'issuer-settings',
            title: t('invoice:nav.issuerSettings.title'),
            icon: faUsersCog,
            permissions: ['user_invoice.view'],
            group: 'settings',
            tooltip: t('invoice:nav.issuerSettings.tooltip')
        }),
        [t]
    );

    const invoiceTabs: Tab[] = useMemo(() => {
        if (!hasSetting) return [issuerTab];
        return [
            // --- Operative Tabs (täglicher Arbeitsablauf) ---
            // Rechnungen UND Gutschriften werden direkt in "Alle Rechnungen" erstellt
            // (Erstellen-Buttons in der Übersicht), daher keine separaten Create-Tabs mehr.
            {
                key: '',
                title: t('invoice:nav.allInvoices'),
                icon: faList,
                tags: [t('invoice:nav.searchTag')],
                permissions: ['user_invoice.view']
            },
            {
                key: 'open-items',
                title: t('invoice:nav.openItems.title'),
                icon: faFileInvoiceDollar,
                permissions: ['user_invoice.view'],
                tooltip: t('invoice:nav.openItems.tooltip')
            },
            {
                key: 'import',
                title: t('invoice:nav.import'),
                icon: faFileImport,
                permissions: ['user_invoice.import']
            },
            {
                key: 'external',
                title: t('invoice:nav.external'),
                icon: faExternalLinkAlt,
                permissions: ['user_invoice.import'],
                experimental: 'ai_v2',
                beta: true
            },
            {
                key: 'archive',
                title: t('invoice:nav.archive'),
                icon: faFileArchive,
                permissions: ['user_invoice.view'],
                group: 'settings'
            },
            // --- Konfiguration / Stammdaten (hinter dem Zahnrad) ---
            issuerTab,
            {
                key: 'payment-terms',
                title: t('invoice:nav.paymentTerms.title'),
                icon: faGavel,
                permissions: ['user_invoice.payment_term.view'],
                group: 'settings',
                tooltip: t('invoice:nav.paymentTerms.tooltip')
            },
            {
                key: 'sepa-mandates',
                title: t('invoice:nav.sepaMandates.title'),
                icon: faFileSignature,
                permissions: ['user_invoice.view'],
                group: 'settings',
                experimental: 'sepa_v1',
                tooltip: t('invoice:nav.sepaMandates.tooltip')
            },
            {
                key: 'sepa-reports',
                title: t('invoice:nav.sepaReports.title'),
                icon: faFileCode,
                permissions: ['user_invoice.view'],
                group: 'settings',
                tooltip: t('invoice:nav.sepaReports.tooltip')
            },
            {
                key: 'products',
                title: t('invoice:nav.products'),
                // Same icon as the shop module's Produkte tab and the ProductList page —
                // both tabs open the identical catalogue, so they must look identical.
                icon: faBoxesStacked,
                permissions: ['user_invoice.product.view'],
                group: 'settings'
            },
            {
                key: 'journal',
                title: t('invoice:nav.journal'),
                icon: faBook,
                permissions: ['user_invoice.journal.view'],
                ...accountingTabProps
            },
            {
                key: 'ledger',
                title: t('invoice:nav.ledger'),
                icon: faTable,
                permissions: ['user_invoice.journal.view'],
                ...accountingTabProps
            },
            {
                key: 'reports',
                title: t('invoice:nav.reports'),
                icon: faChartPie,
                permissions: ['user_invoice.journal.view'],
                ...accountingTabProps
            },
            {
                key: 'debtors',
                title: t('invoice:nav.debtors.title'),
                // Reachable for every club: this view is computed from invoices and
                // payments, so it works without the ledger being switched on.
                icon: faAddressBook,
                permissions: ['user_invoice.view'],
                beta: true,
                group: 'settings'
            },
            {
                key: 'cost-center',
                title: t('invoice:nav.costCenter.title'),
                icon: faPiggyBank,
                permissions: ['user_invoice.cost_center.view'],
                group: 'settings',
                tooltip: t('invoice:nav.costCenter.tooltip')
            },
            {
                key: 'templates',
                title: t('invoice:nav.templates'),
                icon: faPaintBrush,
                permissions: ['user_invoice.template.view'],
                group: 'settings'
            },
            {
                key: 'tools',
                title: t('invoice:nav.tools.title'),
                tooltip: t('invoice:nav.tools.tooltip'),
                icon: faToolbox,
                group: 'settings'
            }
        ];
    }, [accountingTabProps, hasSetting, issuerTab, t]);

    // The banking module hides its data tabs until a bank is connected, so the layout needs to know
    // the connection state for every club. Without `banking.view` this 403s, which the global
    // fetcher swallows — the tabs then stay visible but are hidden by their own permission check.
    const { data: bankConnection } = useSWR<{ data: unknown | null }>(
        isAuthenticated && clubSlug ? bankingKeys.connection(clubSlug) : null
    );
    // Undefined means "still loading" — keep the tabs until we know, so a connected club never
    // sees them collapse and reappear on every page load.
    const hasBankConnection = bankConnection === undefined || !!bankConnection.data;

    const filteredInvoiceTabs = useMemo(
        () => invoiceTabs.filter((tab) => !tab.experimental || experimentalFeatureSet.has(tab.experimental)),
        [experimentalFeatureSet, invoiceTabs]
    );
    const openInvoiceForm = useCallback(
        (e: React.MouseEvent, isCredit = false) => {
            e.stopPropagation();
            addModal({
                title: isCredit ? 'Gutschrift erstellen' : 'Rechnung erstellen',
                show: true,
                size: 80,
                content: (id) => <InvoiceForm isCredit={isCredit} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal]
    );
    const openJournalEntryForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: t('trainingsjournal:actions.manualEntry'),
                show: true,
                size: 80,
                content: (id) => <EntryForm callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, t]
    );
    const openJournalTrainingForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: t('trainingsjournal:trainings.common.create'),
                show: true,
                content: (id) => <TrainingForm callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, t]
    );
    const openEventForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Termin hinzufügen',
                show: true,
                size: 70,
                content: (id) => <EventForm callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal]
    );
    const openMemberForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Mitglied anlegen',
                show: true,
                size: 70,
                content: (id) => <ClubMemberForm clubSlug={clubSlug} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, clubSlug]
    );
    const openAllocationForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Buchung erstellen',
                show: true,
                size: 70,
                content: (id) => <AllocationForm callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal]
    );
    const openVoucherForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Gutschein erstellen',
                show: true,
                size: 60,
                content: (id) => <VoucherForm clubSlug={clubSlug} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, clubSlug]
    );
    const openVirtualKeyForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Schlüssel erstellen',
                show: true,
                size: 60,
                content: (id) => <VirtualKeyForm virtualKeyId={undefined} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal]
    );
    const openPostForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Beitrag erstellen',
                content: (id) => <PostForm callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal]
    );
    const openTrainerBookingForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Trainerbuchung erstellen',
                show: true,
                size: 70,
                content: (id) => <ManualTrainerBookingForm clubSlug={clubSlug} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, clubSlug]
    );
    const openMessageForm = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            addModal({
                title: 'Nachricht verfassen',
                show: true,
                size: 70,
                content: (id) => <MessageComponent clubSlug={clubSlug} callback={() => removeModal(id)} />
            });
        },
        [addModal, removeModal, clubSlug]
    );
    const openChat = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setChatOpen(true);
    }, []);

    const builtPages: Page[] = useMemo(
        () =>
            _pages ||
            buildClubPages({
                clubSlug,
                updateCount,
                allocationRequestCount: allocationRequestCount?.count,
                unreadBookingCount: unreadBookingCount?.count,
                unreadInboxCount: unreadInboxCount?.count,
                invoiceTabs: filteredInvoiceTabs,
                hasBankConnection
            }),
        // `t` rebuilds the pages on language change (buildClubPages resolves titles via i18n)
        [
            _pages,
            clubSlug,
            filteredInvoiceTabs,
            updateCount,
            allocationRequestCount,
            unreadBookingCount,
            unreadInboxCount,
            hasBankConnection,
            t
        ]
    );
    const quickActionsMap = useMemo(
        (): Record<string, NonNullable<Page['quickActions']>> => ({
            ...(hasSetting && {
                invoices: [
                    {
                        label: 'Rechnung erstellen',
                        icon: faPlus,
                        prominent: true,
                        onClick: (e) => openInvoiceForm(e, false)
                    },
                    {
                        label: 'Gutschrift erstellen',
                        icon: faFileInvoiceDollar,
                        onClick: (e) => openInvoiceForm(e, true)
                    }
                ]
            }),
            journal: [
                {
                    label: t('trainingsjournal:actions.manualEntry'),
                    icon: faPlus,
                    prominent: true,
                    onClick: openJournalEntryForm
                },
                {
                    label: t('trainingsjournal:trainings.common.create'),
                    icon: faChalkboardTeacher,
                    prominent: true,
                    onClick: openJournalTrainingForm
                }
            ],
            ...(canCreateEvent && {
                bookings: [{ label: 'Event erstellen', icon: faCalendarPlus, prominent: true, onClick: openEventForm }]
            }),
            members: [{ label: 'Mitglied anlegen', icon: faUserPlus, prominent: true, onClick: openMemberForm }],
            'space-manager': [
                { label: 'Buchung erstellen', icon: faBorderAll, prominent: true, onClick: openAllocationForm }
            ],
            vouchers: [
                { label: 'Gutschein erstellen', icon: faWindowMaximize, prominent: true, onClick: openVoucherForm }
            ],
            access: [{ label: 'Schlüssel erstellen', icon: faKey, prominent: true, onClick: openVirtualKeyForm }],
            feed: [{ label: 'Beitrag erstellen', icon: faNewspaper, prominent: true, onClick: openPostForm }],
            'trainer-booking': [
                {
                    label: 'Trainerbuchung erstellen',
                    icon: faChalkboardTeacher,
                    prominent: true,
                    onClick: openTrainerBookingForm
                }
            ],
            ...(canComposeMessage && {
                messages: [{ label: 'Nachricht verfassen', icon: faNewspaper, prominent: true, onClick: openMessageForm }]
            }),
            // Navigation rather than a form, unlike every other entry here.
            // A training group is a time on a court: it needs a plan to belong
            // to and a lane to sit on, neither of which a modal opened from the
            // club shell knows about. So this opens the week grid of whichever
            // plan the club last worked in — which is where the group is made,
            // by pointing at the slot it goes in.
            'training-planner': [
                {
                    label: t('training-planner:quickAction.openWeek'),
                    icon: faTable,
                    prominent: true,
                    onClick: () => void openPlannerWeek()
                }
            ]
        }),
        [
            canComposeMessage,
            canCreateEvent,
            hasSetting,
            openAllocationForm,
            openChat,
            openEventForm,
            openInvoiceForm,
            openJournalEntryForm,
            openJournalTrainingForm,
            openMemberForm,
            openMessageForm,
            openPostForm,
            openTrainerBookingForm,
            openPlannerWeek,
            openVirtualKeyForm,
            openVoucherForm,
            t
        ]
    );

    const allPages: Page[] = useMemo(
        () =>
            builtPages.map((page) => {
                const segment = page.link.split('/').pop() ?? '';
                const qas = quickActionsMap[segment];
                return qas ? { ...page, quickActions: qas } : page;
            }),
        [builtPages, quickActionsMap]
    );
    // Third navigation axis: experimental flags, on the page and on the tab.
    const visiblePages = useMemo(
        () =>
            allPages
                .filter((page) => !page.experimental || experimentalFeatureSet.has(page.experimental))
                // Tabs can be flagged too — the Einstellungen hub lists modules that are themselves
                // still experimental, and those entries must not leak into clubs without the flag.
                // Same for the history section, whose page every club sees while one of its tabs
                // belongs to a module most of them do not have.
                .map((page) =>
                    page.tabs?.some((tab) => tab.experimental)
                        ? {
                              ...page,
                              tabs: page.tabs.filter(
                                  (tab) => !tab.experimental || experimentalFeatureSet.has(tab.experimental)
                              )
                          }
                        : page
                ),
        [allPages, experimentalFeatureSet]
    );
    // Fourth navigation axis: which modules the club wants to see (club_settings.modules.visible).
    //
    // ⚠️ This is NOT access control. It hides modules, it does not lock them — the route
    // stays reachable, the API stays open, the data stays put. Permissions grant access,
    // visibility creates order. The hard gates are <CheckAccess> and <CheckSubscription>
    // below, plus the server-side permission checks.
    //
    // While the setting is still loading, `visible` is undefined and isModuleVisible()
    // returns true for everything, so the sidebar never renders short and then grows.
    const pages = useMemo(
        () =>
            showAllModules
                ? visiblePages
                : visiblePages.filter((page) => isModuleVisible(visibleModules, page.moduleKey)),
        [visiblePages, visibleModules, showAllModules]
    );
    // Find the plan key required by the currently active page (empty string = pass-through).
    //
    // ⚠️ Resolved against visiblePages, NOT pages: a deep link to a hidden module still
    // renders its content below, and looking the page up in the already-filtered list
    // would yield undefined → '' → CheckSubscription turns into a pass-through, making a
    // paid module freely accessible exactly where it is hidden.
    const currentPagePlan = visiblePages.find((p) => p.link.split('/')[3] === currentPageLink)?.requiredPlan ?? '';
    // The page the user is actually on, used to explain a hidden module instead of just
    // showing an empty sidebar slot.
    const currentPage = visiblePages.find((p) => p.link.split('/')[3] === currentPageLink);
    const currentPageHidden = !showAllModules && !isModuleVisible(visibleModules, currentPage?.moduleKey);
    // Only offer the escape hatch when the club actually hides something.
    const anyModuleHidden = useMemo(
        () =>
            hasHiddenModules(
                visibleModules,
                visiblePages.map((page) => page.moduleKey)
            ),
        [visibleModules, visiblePages]
    );
    const openGlobalSearch = () =>
        addModal({
            title: 'Globale Suche',
            show: true,
            size: 100,
            content: () => <GlobalSearch clubSlug={clubSlug} />
        });

    const setNavItems = useCommandPaletteStore((state) => state.setNavItems);
    const registerNavItems = useCommandPaletteStore((state) => state.registerNavItems);
    const unregisterNavItems = useCommandPaletteStore((state) => state.unregisterNavItems);
    const registerCommands = useCommandPaletteStore((state) => state.registerCommands);
    const unregisterCommands = useCommandPaletteStore((state) => state.unregisterCommands);

    useEffect(() => {
        const DEMOTED_LINKS = [`/club/${clubSlug}/history`];
        // Source is visiblePages, not pages: Cmd+K deliberately still finds hidden
        // modules — the global search is the way back when someone hid too much. They
        // are only demoted and labelled, never removed.
        const navItems = visiblePages.flatMap((page) => {
            const hidden = !showAllModules && !isModuleVisible(visibleModules, page.moduleKey);
            const demoted = DEMOTED_LINKS.includes(page.link) || hidden;
            const suffix = hidden ? ` (${t('sidebar.hiddenSuffix')})` : '';
            return [
                // Virtual pages (e.g. the Einstellungen hub) have no standalone route — skip the
                // page-level entry so Cmd+K never links to a dead URL; their tabs link to real routes.
                ...(page.virtual
                    ? []
                    : [
                          {
                              id: `nav-${page.link}`,
                              label: `${page.title}${suffix}`,
                              url: page.link,
                              icon: page.icon,
                              demoted
                          }
                      ]),
                // External tabs (e.g. Developer Docs) leave the app scope — keep them out of Cmd+K nav.
                ...(page.tabs ?? [])
                    .filter((tab) => !tab.external)
                    .map((tab) => ({
                        id: `nav-${page.link}-${tab.key}`,
                        label: tab.title,
                        module: `${page.title}${suffix}`,
                        url: tab.link ?? (tab.key ? `${page.link}/${tab.key}` : page.link),
                        icon: tab.icon,
                        demoted
                    }))
            ];
        });
        setNavItems(navItems);
    }, [visiblePages, visibleModules, showAllModules, clubSlug, t, setNavItems]);

    // Clear page nav items on unmount only. (Doing this as the page-nav effect's cleanup made it
    // run on every `pages` identity change — e.g. SWR polling — causing a transient empty→full
    // double state-commit and redundant CommandPalette re-renders. settings-section items are
    // preserved by setNavItems regardless.)
    useEffect(() => () => setNavItems([]), [setNavItems]);

    // Available module url segments (last path part), used to gate Cmd+K settings sections
    // so we never surface settings for modules the user doesn't have in their nav.
    const availableSegmentsKey = useMemo(() => pages.map((p) => p.link.split('/').pop() ?? '').join(','), [pages]);

    // Pre-seed settings sections for available modules so they're discoverable in Cmd+K
    // even before the user has navigated to a settings page in this session.
    useEffect(() => {
        if (!clubSlug) return;
        const items = getSettingsSectionNavItems(clubSlug, availableSegmentsKey.split(',').filter(Boolean));
        // Self-healing: purge any stale settings-section entries that aren't part of the
        // canonical set (e.g. a now-unavailable module, or leftovers kept alive by HMR).
        const canonicalIds = new Set(items.map((i) => i.id));
        const stale = useCommandPaletteStore
            .getState()
            .navItems.filter((n) => n.id.startsWith('settings-section::') && !canonicalIds.has(n.id))
            .map((n) => n.id);
        if (stale.length) unregisterNavItems(stale);
        registerNavItems(items);
    }, [clubSlug, availableSegmentsKey, registerNavItems, unregisterNavItems]);

    useEffect(() => {
        // Source is `pages` (fully filtered), not allPages: unlike nav items, a quick
        // action is not a way back — "Rechnung erstellen" for a hidden module is an
        // action with a side effect, so hidden modules contribute no commands.
        const cmds = pages.flatMap((page) =>
            (page.quickActions ?? []).map((qa) => ({
                id: `cmd-${page.link}-${qa.label}`,
                label: qa.label,
                module: page.title,
                icon: qa.icon,
                onSelect: () => qa.onClick({ stopPropagation: () => {} } as React.MouseEvent)
            }))
        );
        registerCommands(cmds);
        return () => unregisterCommands(cmds.map((c) => c.id));
    }, [pages, registerCommands, unregisterCommands]);

    useEffect(() => {
        const handleRouteChange = (_, { shallow }) => setIsLoadingPage(!shallow);
        const handleRouteChangeComplete = () => setIsLoadingPage(false);

        events.on('routeChangeStart', handleRouteChange);
        events.on('routeChangeComplete', handleRouteChangeComplete);
        // A navigation the page refused — the CMS screens throw out of
        // `routeChangeStart` when there is unsaved work and the person says "stay" —
        // never completes. Without this, the overlay it started stayed up for good.
        events.on('routeChangeError', handleRouteChangeComplete);

        return () => {
            events.off('routeChangeStart', handleRouteChange);
            events.off('routeChangeComplete', handleRouteChangeComplete);
            events.off('routeChangeError', handleRouteChangeComplete);
        };
    }, [events]);

    return (
        <>
            <div className="tw-box-border tw-grid tw-h-screen tw-overflow-hidden *:tw-min-h-0 md:tw-grid-cols-[250px_minmax(0,_1fr)] lg:tw-grid-cols-[300px_minmax(0,_1fr)]">
                {pages && (
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-r tw-border-solid tw-border-accents-2">
                        <div className="tw-mt-2 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-2">
                            <Dropdown
                                placement="bottom-start"
                                triggerElement={
                                    <button
                                        type="button"
                                        className="tw-mb-2 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-p-3 tw-text-accents-9"
                                    >
                                        {isLoadingClub ? (
                                            <Spinner size="sm" color="currentColor" className="tw-size-6" />
                                        ) : (
                                            <>
                                                <div className="tw-relative tw-flex tw-size-6 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full">
                                                    {!pageLogoError &&
                                                    !isLoadingClub &&
                                                    club?.data?.images?.logo.length > 0 ? (
                                                        <Image
                                                            src={imageSource(club?.data.images.logo[0])}
                                                            alt={club?.data?.name || 'Club Logo'}
                                                            fill
                                                            style={{ objectFit: 'cover' }}
                                                            onError={() => setPageLogoError(true)}
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faStoreAlt} />
                                                    )}
                                                </div>
                                                <h2 className="tw-mb-0 tw-flex-1 tw-truncate tw-text-base tw-font-semibold">
                                                    {club?.data?.title}
                                                </h2>
                                            </>
                                        )}
                                    </button>
                                }
                            >
                                {user?.clubs.map((c) => (
                                    <Dropdown.Item
                                        link
                                        icon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                                        key={c.slug}
                                        href={{
                                            pathname,
                                            query: { ...query, slug: c.slug }
                                        }}
                                        label={c.title}
                                    />
                                ))}
                            </Dropdown>
                            <Button onClick={openGlobalSearch} className="tw-mb-2" block>
                                <FontAwesomeIcon icon={faSearch} />
                                <span className="tw-text-sm tw-font-medium">Globale Suche</span>
                            </Button>
                            <AnimatePresence initial={false}>
                                {favorites.length > 0 && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2"
                                    >
                                        <div className="tw-flex tw-flex-col tw-gap-2 tw-py-2">
                                            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
                                                <span className="tw-text-xs tw-font-semibold tw-text-accents-9">
                                                    Favoriten
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={resetFavorites}
                                                    className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-xs tw-text-accents-5"
                                                >
                                                    leeren
                                                </button>
                                            </div>
                                            <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                                                {favorites.map((fav) => (
                                                    <Tooltip
                                                        tooltip={`${fav.pageTitle} / ${fav.title} `}
                                                        key={fav.id}
                                                        placement="bottom-end"
                                                    >
                                                        <Link
                                                            key={fav.id}
                                                            href={fav.link ?? `${fav.pageLink}/${fav.key}`}
                                                            className="tw-card-bordered tw-flex tw-items-center tw-justify-center tw-text-accents-8 tw-transition-colors hover:tw-border-success hover:tw-text-primary"
                                                        >
                                                            <FontAwesomeIcon key={fav.id} icon={fav.icon} />
                                                        </Link>
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <ScrollArea>
                            <SideBarAccordion
                                ref={accordionRef}
                                pages={pages}
                                currentPath={currentPath}
                                isActiveTab={isActive}
                                pageIsActive={pageIsActive}
                                favorites={favorites}
                                addFavorite={addFavorite}
                                removeFavorite={removeFavorite}
                                subscribedPlans={subscribedPlans}
                            />
                        </ScrollArea>
                        <div className="tw-mt-2 tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-accents-1 tw-px-3 tw-pt-2">
                            <div className="tw-min-w-0 tw-flex-1">
                                <LanguageDropdown full placement="top" />
                            </div>
                            <div className="tw-flex-none">
                                <InfoTooltip tooltip={t('languageSwitch.tooltip')} />
                            </div>
                        </div>
                        <div className="tw-px-3">
                            <div className="tw-card-bordered tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-bg-primary/10 tw-p-3">
                                <div className="tw-flex tw-flex-col tw-gap-1">
                                    <p className="tw-m-0 tw-text-xs tw-font-medium tw-text-success">
                                        {t('sidebar.needHelp')}
                                    </p>
                                </div>

                                <Button
                                    color="success"
                                    block
                                    className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-py-2.5 tw-text-sm tw-font-bold"
                                    onClick={openChat}
                                >
                                    <FontAwesomeIcon icon={faComments} />
                                    <span>{t('sidebar.startChat')}</span>
                                </Button>
                            </div>
                        </div>
                        <div className="tw-mx-3 tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <Button.Link
                                href={pageReturnLink ?? `/club/${clubSlug}`}
                                className="tw-w-full"
                                color="success"
                                size="sm"
                            >
                                {t('sidebar.back')}
                            </Button.Link>
                            <Button
                                size="sm"
                                aria-label={t('sidebar.expandAll')}
                                title={t('sidebar.expandAll')}
                                icon={<FontAwesomeIcon icon={faExpand} />}
                                onPress={() => accordionRef.current?.expandAll()}
                            />
                            <Button
                                size="sm"
                                aria-label={t('sidebar.collapseAll')}
                                title={t('sidebar.collapseAll')}
                                icon={<FontAwesomeIcon icon={faCompress} />}
                                onPress={() => accordionRef.current?.collapseAll()}
                            />
                            {anyModuleHidden && (
                                // Escape hatch: reveals hidden modules for this user and this
                                // session only — it never touches the club setting.
                                <Button
                                    size="sm"
                                    color={showAllModules ? 'primary' : 'light'}
                                    aria-label={t('sidebar.showAllModules')}
                                    title={t('sidebar.showAllModules')}
                                    icon={<FontAwesomeIcon icon={showAllModules ? faEye : faEyeSlash} />}
                                    onPress={() => setShowAllModules(!showAllModules)}
                                />
                            )}
                            <Dropdown
                                placement="top"
                                triggerElement={
                                    <Button
                                        color="light"
                                        size="sm"
                                        aria-label={t('sidebar.help')}
                                        icon={<FontAwesomeIcon icon={faCircleQuestion} />}
                                    />
                                }
                            >
                                <DropdownItem
                                    onClick={() =>
                                        addModal({
                                            title: t('shortcuts.modalTitle'),
                                            content: <ShortcutsPanel />,
                                            size: 60
                                        })
                                    }
                                >
                                    <FontAwesomeIcon icon={faKeyboard} className="tw-w-4" />
                                    {t('sidebar.shortcuts')}
                                </DropdownItem>
                                {user?.hasClub && (
                                    <DropdownItem
                                        onClick={() =>
                                            addModal({
                                                title: t('changelog.title'),
                                                content: <ChangelogArchive />,
                                                size: 45
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faNewspaper} className="tw-w-4" />
                                        {t('sidebar.changelog')}
                                    </DropdownItem>
                                )}
                            </Dropdown>
                        </div>
                    </div>
                )}
                <div className="tw-relative">
                    <div className="tw-flex tw-h-full tw-overflow-auto tw-p-4 tw-pr-0">
                        <AnimatePresence>
                            {isLoadingPage && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="tw-absolute tw-inset-0 tw-z-[999] tw-flex tw-items-center tw-justify-center tw-bg-white/20 tw-backdrop-blur-xl"
                                >
                                    <Spinner size="lg" color="success" />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="tw-flex tw-w-full tw-flex-col">
                            {currentPageHidden && currentPage && (
                                // Explains the deep link instead of leaving the user on a page
                                // whose sidebar entry is gone. Sits above CheckAccess because it
                                // explains the visibility, not the content.
                                <HiddenModuleBanner
                                    clubSlug={clubSlug}
                                    moduleKey={currentPage.moduleKey}
                                    moduleTitle={currentPage.title}
                                    visible={visibleModules}
                                    onRevealed={() => mutateVisibleModules()}
                                    onShowAllForSession={() => setShowAllModules(true)}
                                />
                            )}
                            <CheckAccess clubSlug={clubSlug} permission={permissions}>
                                <CheckSubscription clubSlug={clubSlug} permission={currentPagePlan}>
                                    <AppSupportChatSplitLayout
                                        clubSlug={clubSlug}
                                        open={chatOpen}
                                        onOpenChange={setChatOpen}
                                    >
                                        {children}
                                    </AppSupportChatSplitLayout>
                                </CheckSubscription>
                            </CheckAccess>
                        </div>
                    </div>
                </div>
            </div>
            <GlobalOverlay />
            <ChangelogPopup />
            <AnnouncementModal id="support-urlaub-2026-07" endDate="2026-07-13" title="Hinweis zum Support">
                <div className="tw-flex tw-flex-col tw-gap-3 tw-text-accents-8">
                    <p>
                        Aktuell ist Urlaubszeit! Da ein Teil unseres Teams neue Energie tankt, arbeiten wir im Support
                        gerade in einer kleineren Besetzung.
                    </p>

                    <ul>
                        <li>
                            <strong>Erreichbarkeit:</strong> Du erreichst uns aktuell ausschließlich per{' '}
                            <strong>Ticket oder E-Mail</strong>. Unser telefonischer Support pausiert.
                        </li>
                        <li>
                            <strong>Antwortzeiten:</strong> Bitte hab etwas Geduld, falls unsere Rückmeldung etwas
                            länger dauert als gewohnt.
                        </li>
                    </ul>

                    <p>
                        Vielen Dank für dein Verständnis. Ab dem <strong>13.07.</strong> sind wir wieder mit voller
                        Power für dich im Einsatz!
                    </p>
                </div>
            </AnnouncementModal>
        </>
    );
};
